import { useEffect, useSyncExternalStore } from 'react'

/**
 * Who may show their full-size file, and when
 * -------------------------------------------------------------
 * Zoomed in past what its drawing copy can show, a picture lays the full-size
 * file over it (nodes.tsx, SHARP AT ANY ZOOM). A full-size photograph is tens
 * of megabytes once decoded, and a performance recording on Huanrui's laptop
 * (2026-10-02) showed what that costs: every zoom set off a burst of decoding
 * and of pictures being handed to the graphics card, and frames were dropped
 * in exactly those moments. Two rules, neither of which costs any sharpness
 * on what is being looked at:
 *
 *   WHEN THE BOARD IS STILL. Nothing new is fetched or swapped in while the
 *   board is zoomed, panned or something on it dragged; once it has been still
 *   for SETTLE_MS, the pictures that want it get it. A picture already sharp
 *   stays sharp through the movement.
 *
 *   NO MORE THAN MAX_FULL AT ONCE (3, Huanrui). Of the pictures that want it,
 *   the ones nearest the middle of the view. A picture that leaves the view
 *   is taken off the board altogether (onlyRenderVisibleElements), and lets
 *   go of its turn at once.
 */

const SETTLE_MS = 300
const MAX_FULL = 3

const wanting = new Set<string>()
let granted = new Set<string>()
let settled = true
let settleTimer = 0
const listeners = new Set<() => void>()
/** How far a node is from the middle of the view, on screen; set by the canvas. */
let distanceOf: (id: string) => number = () => 0

function emit(): void {
  for (const listener of listeners) listener()
}

/** Decide again who holds a full-size file. Only while the board is still. */
function regrant(): void {
  if (!settled) return
  const order = [...wanting].sort((a, b) => distanceOf(a) - distanceOf(b))
  const next = new Set(order.slice(0, MAX_FULL))
  if (next.size === granted.size && [...next].every((id) => granted.has(id))) return
  granted = next
  emit()
}

/** The canvas says how to measure nearness to the middle of the view. */
export function setFullSizeDistance(measure: (id: string) => number): void {
  distanceOf = measure
}

/**
 * The board moved: zoom, pan or a drag. Called on every step; the pictures
 * wait until it has been still for SETTLE_MS.
 */
export function boardMoved(): void {
  settled = false
  window.clearTimeout(settleTimer)
  settleTimer = window.setTimeout(() => {
    settled = true
    regrant()
  }, SETTLE_MS)
}

function setWanted(id: string, yes: boolean): void {
  if (yes) {
    if (wanting.has(id)) return
    wanting.add(id)
    regrant()
    return
  }
  if (!wanting.delete(id)) return
  if (granted.delete(id)) {
    granted = new Set(granted)
    emit()
  }
  // Its turn goes to the next nearest, once the board is still.
  regrant()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Whether this picture may show its full-size file now. It says whether it
 * wants to (drawn larger than its copy), and lets go when it is taken off the
 * board.
 */
export function useFullSizeTurn(id: string, wants: boolean): boolean {
  useEffect(() => {
    setWanted(id, wants)
  }, [id, wants])
  useEffect(() => () => setWanted(id, false), [id])
  return useSyncExternalStore(
    subscribe,
    () => granted.has(id),
    () => false,
  )
}
